import {
  Card, CardMedia, CardContent, CardActions, Box, Chip, Typography, Button,
} from '@mui/material'
import { motion } from 'framer-motion'
import LocalOfferRoundedIcon from '@mui/icons-material/LocalOfferRounded'
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded'
import OfferCountdown from 'components/OfferCountdown'
import { discountLabel, formatOfferDateTime, getOfferCountdownTarget } from 'utils/offers'
import { DisplayCurrency } from 'components'

const GRADIENTS = [
  'linear-gradient(135deg, #6366F1 0%, #8B5CF6 55%, #EC4899 100%)',
  'linear-gradient(135deg, #0EA5E9 0%, #6366F1 100%)',
  'linear-gradient(135deg, #F59E0B 0%, #EF4444 100%)',
  'linear-gradient(135deg, #10B981 0%, #059669 100%)',
]

/** Compute price display for the targeted product */
const computeOfferPrice = (product) => {
  const base = parseFloat(product.base_price) || 0
  const sale = parseFloat(product.sale_price) || 0
  if (sale > 0 && sale < base) {
    return { finalPrice: sale, originalPrice: base }
  }
  return { finalPrice: base, originalPrice: base }
}

const OfferCard = ({ offer, index = 0, onClick, compact = false }) => {
  const gradient = GRADIENTS[index % GRADIENTS.length]
  const hasBanner = Boolean(offer.bannerImage)
  const countdown = getOfferCountdownTarget(offer)

  // If this offer targets a specific product, show its price info
  const targetProduct = offer.target_product || null
  const priceInfo = targetProduct ? computeOfferPrice(targetProduct) : null
  const productName = targetProduct?.name || null
  const productImage = targetProduct?.thumbnail_url || targetProduct?.primary_image || null

  return (
    <Card
      component={motion.div}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.08, duration: 0.4 }}
      onClick={onClick}
      sx={{
        borderRadius: 4,
        overflow: 'hidden',
        cursor: onClick ? 'pointer' : 'default',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        transition: 'transform 0.25s ease, box-shadow 0.25s ease',
        '&:hover': onClick ? {
          transform: 'translateY(-6px)',
          boxShadow: (t) => t.palette.mode === 'dark'
            ? '0 20px 48px rgba(0,0,0,0.5)'
            : '0 20px 48px rgba(99,102,241,0.18)',
        } : {},
      }}
    >
      <Box sx={{ position: 'relative', height: compact ? 160 : { xs: 180, md: 200 } }}>
        {hasBanner ? (
          <CardMedia
            component="img"
            image={offer.bannerImage}
            alt={offer.title}
            sx={{ height: '100%', objectFit: 'cover' }}
          />
        ) : productImage ? (
          <CardMedia
            component="img"
            image={productImage}
            alt={productName || offer.title}
            sx={{ height: '100%', objectFit: 'contain', background: '#f9f9f9' }}
          />
        ) : (
          <Box
            sx={{
              height: '100%',
              background: gradient,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <LocalOfferRoundedIcon sx={{ fontSize: 64, color: 'rgba(255,255,255,0.35)' }} />
          </Box>
        )}
        <Chip
          label={discountLabel(offer)}
          size="small"
          color="secondary"
          sx={{
            position: 'absolute',
            top: 12,
            right: 12,
            fontWeight: 800,
            fontSize: '0.75rem',
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
          }}
        />
      </Box>

      <CardContent sx={{ flex: 1, p: compact ? 2 : 2.5 }}>
        <Typography variant={compact ? 'subtitle1' : 'h6'} fontWeight={800} gutterBottom>
          {offer.title}
        </Typography>

        {/* Show targeted product name if this offer is for a specific product */}
        {productName && (
          <Typography variant="body2" fontWeight={600} color="text.primary" sx={{ mb: 0.5 }}>
            {productName}
          </Typography>
        )}

        {offer.description && (
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{
              mb: 2,
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
            }}
          >
            {offer.description}
          </Typography>
        )}

        {/* Show actual product price with offer discount applied */}
        {priceInfo && (
          <Box display="flex" alignItems="center" gap={1} mb={1}>
            <Typography variant="subtitle1" fontWeight={800} color="primary.main">
              <DisplayCurrency number={priceInfo.finalPrice} />
            </Typography>
            {priceInfo.originalPrice > priceInfo.finalPrice && (
              <Typography
                variant="body2"
                sx={{ textDecoration: 'line-through', color: 'text.secondary' }}
              >
                <DisplayCurrency number={priceInfo.originalPrice} />
              </Typography>
            )}
          </Box>
        )}

        {countdown ? (
          <OfferCountdown
            targetDate={countdown.target}
            label={countdown.label}
            dateTime={`${countdown.dateLabel} ${formatOfferDateTime(countdown.target)}`}
            compact={compact}
          />
        ) : (
          <Typography variant="caption" color="text.secondary" fontWeight={600}>
            Limited time offer
          </Typography>
        )}
      </CardContent>

      {onClick && (
        <CardActions sx={{ px: 2.5, pb: 2.5, pt: 0 }}>
          <Button
            fullWidth
            variant="contained"
            endIcon={<ArrowForwardRoundedIcon />}
            sx={{ borderRadius: 2.5, fontWeight: 700, textTransform: 'none', py: 1 }}
          >
            Shop now
          </Button>
        </CardActions>
      )}
    </Card>
  )
}

export default OfferCard
